/**
 * @fileoverview Streaks, goal progress and the activity heatmap.
 * @module lib/domain/stats
 *
 * @description
 * Pure functions over plain data: no storage, no React, no I/O. That is what
 * makes them trivially testable, and what will let them run unchanged the day
 * the data comes from a real backend instead of the browser.
 */

import type {
	BestStreak,
	GlobalStats,
	GoalProgress,
	Habit,
	HabitEntry,
	HabitSchedule,
	HabitStats,
	HeatmapCell,
} from "@/types/types"

import {
	daysBetween,
	isDueOn,
	parseDateKey,
	todayKey,
	toDateKey,
} from "./schedule"

/** Safety cap on how far back a streak is computed, in days (~10 years). */
const MAX_STREAK_LOOKBACK_DAYS = 3650

/**
 * Progress of a habit toward its own goal.
 *
 * @param habit - The habit to evaluate.
 * @returns Its ratio, whether the goal is reached, and reps remaining.
 *
 * @example
 * // A habit with a 66-rep goal, 40 reps logged
 * computeGoalProgress(habit) // => { ratio: 0.606..., isReached: false, remaining: 26 }
 */
export function computeGoalProgress(habit: Habit): GoalProgress {
	const safeGoal = habit.goalReps > 0 ? habit.goalReps : 1
	const ratio = Math.min(1, Math.max(0, habit.totalReps / safeGoal))

	return {
		ratio,
		isReached: habit.totalReps >= safeGoal,
		remaining: Math.max(0, safeGoal - habit.totalReps),
	}
}

/**
 * Every date a schedule is due on, inclusive, ascending.
 *
 * @param schedule - The habit's recurring period.
 * @param createdAt - ISO-8601 creation date.
 * @param today - Calendar key to stop at. Defaults to today.
 * @returns Due dates from creation to `today`, capped at
 *   {@link MAX_STREAK_LOOKBACK_DAYS} for safety.
 */
function dueDatesSince(
	schedule: HabitSchedule,
	createdAt: string,
	today: string = todayKey()
): string[] {
	const createdKey = toDateKey(new Date(createdAt))
	const span = Math.min(
		Math.max(0, daysBetween(createdKey, today)),
		MAX_STREAK_LOOKBACK_DAYS
	)

	const cursor = parseDateKey(today)
	cursor.setDate(cursor.getDate() - span)

	const dates: string[] = []
	for (let i = 0; i <= span; i++) {
		const key = toDateKey(cursor)
		if (isDueOn(schedule, key, createdAt)) dates.push(key)
		cursor.setDate(cursor.getDate() + 1)
	}

	return dates
}

/**
 * Current and longest streaks of consecutive due dates completed.
 *
 * @description
 * Today gets a grace period: if it is due but not logged yet, the streak is
 * not considered broken until the day is over. Every earlier due date must
 * have an entry, or the current streak stops there.
 *
 * @param schedule - The habit's recurring period.
 * @param createdAt - ISO-8601 creation date.
 * @param loggedDates - Calendar dates this habit has an entry for.
 * @param today - Calendar key for "today". Defaults to today.
 * @returns The current and longest streak, in due-date count.
 */
export function computeHabitStreak(
	schedule: HabitSchedule,
	createdAt: string,
	loggedDates: ReadonlySet<string>,
	today: string = todayKey()
): { currentStreak: number; longestStreak: number } {
	const dueDates = dueDatesSince(schedule, createdAt, today)

	let index = dueDates.length - 1
	if (index >= 0 && dueDates[index] === today && !loggedDates.has(today)) {
		index -= 1
	}

	let currentStreak = 0
	while (index >= 0) {
		const date = dueDates[index]
		if (date === undefined || !loggedDates.has(date)) break
		currentStreak += 1
		index -= 1
	}

	let running = 0
	let longestStreak = 0
	for (const date of dueDates) {
		if (loggedDates.has(date)) {
			running += 1
			longestStreak = Math.max(longestStreak, running)
		} else {
			running = 0
		}
	}
	longestStreak = Math.max(longestStreak, currentStreak)

	return { currentStreak, longestStreak }
}

/**
 * Streak and volume figures for a single habit.
 *
 * @param habit - The habit to evaluate.
 * @param entries - Every logged entry (any habit); filtered internally.
 * @returns Current/longest streak, total reps and the last log date.
 */
export function computeHabitStats(
	habit: Habit,
	entries: readonly HabitEntry[]
): HabitStats {
	const own = entries.filter(entry => entry.habitId === habit.id)
	const loggedDates = new Set(own.map(entry => entry.date))

	const { currentStreak, longestStreak } = computeHabitStreak(
		habit.schedule,
		habit.createdAt,
		loggedDates
	)

	let lastLoggedAt: string | null = null
	for (const entry of own) {
		if (lastLoggedAt === null || entry.date > lastLoggedAt) {
			lastLoggedAt = entry.date
		}
	}

	return {
		currentStreak,
		longestStreak,
		totalReps: habit.totalReps,
		lastLoggedAt,
	}
}

/**
 * Aggregate figures across every tracked habit.
 *
 * @param habits - Every tracked habit.
 * @param entries - Every logged entry.
 * @param today - Calendar key for "today". Defaults to today.
 * @returns Totals, today's due/done counts, and the best current streak.
 */
export function computeGlobalStats(
	habits: readonly Habit[],
	entries: readonly HabitEntry[],
	today: string = todayKey()
): GlobalStats {
	let dueToday = 0
	let doneToday = 0
	let bestStreak: BestStreak | null = null

	for (const habit of habits) {
		const due = isDueOn(habit.schedule, today, habit.createdAt)
		if (due) dueToday += 1

		const own = entries.filter(entry => entry.habitId === habit.id)
		if (due && own.some(entry => entry.date === today)) doneToday += 1

		const loggedDates = new Set(own.map(entry => entry.date))
		const { currentStreak } = computeHabitStreak(
			habit.schedule,
			habit.createdAt,
			loggedDates,
			today
		)

		if (
			currentStreak > 0 &&
			(bestStreak === null || currentStreak > bestStreak.streak)
		) {
			bestStreak = {
				habitId: habit.id,
				name: habit.name,
				streak: currentStreak,
			}
		}
	}

	return {
		habitCount: habits.length,
		totalReps: habits.reduce((sum, habit) => sum + habit.totalReps, 0),
		totalEntries: entries.length,
		doneToday,
		dueToday,
		bestStreak,
	}
}

/**
 * Builds the activity heatmap: one cell per day, reps summed across every
 * habit, over a trailing window ending today.
 *
 * @param entries - Every logged entry.
 * @param options - Window size in weeks (default 53) and the end date.
 * @returns Cells oldest first, each with a 0-4 visual intensity level.
 *
 * @example
 * buildHeatmap(entries, { weeks: 26 }) // roughly the last six months
 */
export function buildHeatmap(
	entries: readonly HabitEntry[],
	options: { readonly weeks?: number; readonly today?: string } = {}
): HeatmapCell[] {
	const weeks = options.weeks ?? 53
	const today = options.today ?? todayKey()

	const countByDate = new Map<string, number>()
	for (const entry of entries) {
		countByDate.set(
			entry.date,
			(countByDate.get(entry.date) ?? 0) + entry.reps
		)
	}

	const end = parseDateKey(today)
	// End the window on the Saturday of today's week, so full weeks line up
	// into calendar columns the way GitHub/GitLab's graph does.
	end.setDate(end.getDate() + (6 - end.getDay()))

	const totalDays = weeks * 7
	const cursor = new Date(end)
	cursor.setDate(cursor.getDate() - (totalDays - 1))

	const max = Math.max(0, ...countByDate.values())

	const cells: HeatmapCell[] = []
	for (let i = 0; i < totalDays; i++) {
		const key = toDateKey(cursor)
		const count = countByDate.get(key) ?? 0
		cells.push({ date: key, count, level: levelFor(count, max) })
		cursor.setDate(cursor.getDate() + 1)
	}

	return cells
}

/**
 * Buckets a day's count into a 0-4 visual intensity level.
 *
 * @param count - Reps logged that day.
 * @param max - The highest single-day count in the visible window.
 * @returns 0 for no activity, up to 4 for the most active days.
 */
function levelFor(count: number, max: number): 0 | 1 | 2 | 3 | 4 {
	if (count <= 0 || max <= 0) return 0
	const ratio = count / max
	if (ratio >= 0.75) return 4
	if (ratio >= 0.5) return 3
	if (ratio >= 0.25) return 2
	return 1
}

/**
 * Formats a ratio as a whole percentage.
 *
 * @param ratio - Value between 0 and 1.
 * @returns A string such as `"46 %"`.
 */
export function formatRatio(ratio: number): string {
	return `${String(Math.round(ratio * 100))} %`
}
